/**
 * A receipt read the way the apps read one: the spec for its input, the
 * built-in steps, and what the model calls cost. The phone runs it with its
 * own key; the companion server runs it with the operator's, for a reading a
 * phone handed over. Same spec, same steps, so a receipt reads the same
 * wherever it is read.
 */

import { readingFrom } from './resolve.js';
import { runReading, stepRegistry } from './runner.js';
import { LINES_SPEC, LOCAL_LINES_SPEC, LOCAL_PHOTO_SPEC, PHOTO_SPEC } from './specs.js';
import { BUILTIN_STEPS } from './steps/index.js';
import type { GroundingFacts } from './steps/ground-items.js';
import type { ModelResponse, ModelTransport, ReadingInput, ReadingState, Spec } from './types.js';

const REGISTRY = stepRegistry(...BUILTIN_STEPS);

/** A runner's two specs: with OCR text to point at, and without. */
export interface ReadingSpecs {
  text: Spec;
  photo: Spec;
}

/** A cloud model's: the phone with its own key, and the server for a phone's handoff. */
export const CLOUD_SPECS: ReadingSpecs = { text: LINES_SPEC, photo: PHOTO_SPEC };

/** A small local model's: the companion server's background worker. */
export const LOCAL_SPECS: ReadingSpecs = { text: LOCAL_LINES_SPEC, photo: LOCAL_PHOTO_SPEC };

/**
 * The local specs with the app's filing call - the list described
 * (`describedCategories`) and the compact answer - the call the categorizer
 * was trained on and the larger local models were measured in: the server
 * worker's on llama.cpp. Its transport then picks the answer's layout
 * (`filingLayout`).
 */
export const LOCAL_DESCRIBED_SPECS: ReadingSpecs = {
  text: withDescribedFiling(LOCAL_LINES_SPEC),
  photo: withDescribedFiling(LOCAL_PHOTO_SPEC),
};

function withDescribedFiling(spec: Spec): Spec {
  return spec.map((entry) => {
    if (entry.step !== 'model-categorize') return entry;
    const options = entry.options as { in: string[] };
    return { ...entry, options: { ...options, in: [...new Set([...options.in, 'described'])], format: 'compact' } };
  });
}

/**
 * The text spec when there is OCR text for the model to point at, else the
 * photo alone - OCR is a separate job, and a receipt may have none. Without
 * correction, the correcting read is left out.
 */
export function readingSpecFor(hasText: boolean, correction: boolean, specs: ReadingSpecs = CLOUD_SPECS): Spec {
  const spec = hasText ? specs.text : specs.photo;
  return correction ? spec : spec.filter((entry) => entry.step !== 'model-correct');
}

export interface ReceiptReading {
  /** The chosen fields as a reading, for `normalizeExtraction`. */
  raw: Record<string, unknown>;
  state: ReadingState;
  /** Model calls made, and what they took and gave, summed. */
  calls: number;
  inputTokens: number | null;
  outputTokens: number | null;
  /** The model that answered last. */
  model: string | null;
  /** A call's schema was refused and it was asked for plain JSON. */
  fallbackUsed: boolean;
  durationMs: number;
}

export interface ReadReceiptRun {
  transport: ModelTransport;
  input: ReadingInput;
  correction: boolean;
  /** Which specs to read with; {@link CLOUD_SPECS} unless the model is a small local one. */
  specs?: ReadingSpecs;
  /**
   * Asked before each model call: once the receipt has moved on, no more is
   * spent on it and the reading ends as `stale`. A runner with nothing to
   * go stale leaves it out.
   */
  isCurrent?: () => Promise<boolean>;
  /** Told of each answer as it comes, before anything else can throw: it has been paid for. */
  onResponse?: (response: ModelResponse) => void;
}

class StaleReading extends Error {}

const add = (a: number | null, b: number | null): number | null => (a === null && b === null ? null : (a ?? 0) + (b ?? 0));

/** Reads the receipt with the spec for its input. Throws a required step's failure: the first model call's. */
export async function readReceipt(run: ReadReceiptRun & { isCurrent: () => Promise<boolean> }): Promise<ReceiptReading | 'stale'>;
export async function readReceipt(run: Omit<ReadReceiptRun, 'isCurrent'>): Promise<ReceiptReading>;
export async function readReceipt(run: ReadReceiptRun): Promise<ReceiptReading | 'stale'> {
  const spec = readingSpecFor(Boolean(run.input.text?.trim()), run.correction, run.specs);
  const modelSteps = new Set(spec.filter((entry) => entry.step.startsWith('model-')).map((entry) => entry.id ?? entry.step));
  const usage: Omit<ReceiptReading, 'raw' | 'state' | 'durationMs'> = {
    calls: 0,
    inputTokens: null,
    outputTokens: null,
    model: null,
    fallbackUsed: false,
  };
  const transport: ModelTransport = {
    ...run.transport,
    async send(request) {
      const response = await run.transport.send(request);
      run.onResponse?.(response);
      usage.calls += 1;
      usage.inputTokens = add(usage.inputTokens, response.inputTokens);
      usage.outputTokens = add(usage.outputTokens, response.outputTokens);
      usage.model = response.model;
      usage.fallbackUsed ||= response.structuredOutputFallback === true;
      return response;
    },
  };

  const isCurrent = run.isCurrent;
  let stale = false;
  const started = Date.now();
  let state: ReadingState;
  try {
    state = await runReading(spec, REGISTRY, run.input, {
      transport,
      ...(isCurrent && {
        async around<T>(id: string, work: () => Promise<T>): Promise<T> {
          // Once stale, every later step throws too: an optional one's failure is only traced, a resolve's ends the run.
          if (!stale && modelSteps.has(id)) stale = !(await isCurrent());
          if (stale) throw new StaleReading(`stale before ${id}`);
          return work();
        },
      }),
    });
  } catch (error) {
    if (stale) return 'stale';
    throw error;
  }
  return { ...usage, raw: readingFrom(state.resolved), state, durationMs: Date.now() - started };
}

/** Whether the chosen reading is a correcting read's. */
export function wasCorrected(state: ReadingState): boolean {
  return Object.values(state.resolved).some((candidate) => candidate?.source === 'model-correct');
}

/** A model step left out the OCR text or lines to fit its budget. */
export function ocrTextOmitted(state: ReadingState): boolean {
  return state.trace.some((trace) => trace.dropped?.some((block) => block === 'lines' || block === 'ocr-text'));
}

/** The category list did not fit, and no filing call made up for it. */
export function categoriesOmitted(state: ReadingState): boolean {
  return state.facts.categoriesDropped === true && !state.trace.some((trace) => trace.step === 'model-categorize' && trace.status === 'ok');
}

/**
 * What reading the amounts off the lines changed, when the items chosen are
 * the grounded ones; otherwise null. Facts, not words: each app says them in
 * its own.
 */
export function groundingChanges(state: ReadingState): GroundingFacts | null {
  const grounding = state.facts.grounding as GroundingFacts | undefined;
  if (!grounding || state.resolved.items?.source !== 'ground-items') return null;
  return grounding;
}
